import { apiAxios } from '@/api'
import { keyBy } from 'lodash'
import { fetchWrapper } from '../../reducers/fetch'
import { PlaidLinkOnSuccessMetadata } from 'react-plaid-link/src/types'
import {
  receivePlaidItems,
  PlaidItem,
} from '../../reducers/finances/plaidItemReducer'
import { fetchFinancialAccountsIfNeeded } from '../financialAccountActions'

export const fetchLinkToken = (redirectUri: string | undefined) =>
  fetchWrapper({
    defaultErrorMessage: 'Unable to grab token',
    fetchFunction: () =>
      apiAxios
        .get<{ link_token: string }>('/finances/api/v1/plaid/link_token', {
          params: {
            redirectUri,
          },
        })
        .then((json) => json.data),
  })

export const fetchUpdateLinkToken = (
  redirectUri: string | undefined,
  plaidItemId: number | null,
  disconnected = false,
  automaticStatements = false
) =>
  fetchWrapper({
    defaultErrorMessage: 'Unable to grab Plaid update link',
    fetchFunction: () => {
      if (!plaidItemId) {
        return null
      }
      const params = {
        redirectUri,
        automaticStatements: automaticStatements || undefined,
        disconnected: disconnected || undefined,
      }
      return apiAxios
        .get<{
          link_token: string
        }>(`/finances/api/v1/plaid/update_link_token/${plaidItemId}`, {
          params,
        })
        .then((json) => json.data)
    },
  })

export const FETCH_PLAID_ACCESS_TOKEN_KEY = 'FETCH_PLAID_ACCESS_TOKEN_KEY'
export const fetchAccessToken = (
  publicToken: string,
  metadata: PlaidLinkOnSuccessMetadata
) =>
  fetchWrapper({
    defaultErrorMessage: 'Unable to grab token',
    fetchKey: FETCH_PLAID_ACCESS_TOKEN_KEY,
    fetchFunction: (dispatch) =>
      apiAxios
        .post<PlaidItem[]>('/finances/api/v1/plaid/access_token', {
          public_token: publicToken,
          metadata,
        })
        .then((json) => {
          dispatch(fetchFinancialAccountsIfNeeded(true))
          dispatch(receivePlaidItems(keyBy(json.data, 'id')))
          return json.data
        }),
  })

export enum TransactionSyncStatus {
  CONNECTING = 'CONNECTING',
  SYNCING = 'SYNCING',
  ERROR = 'ERROR',
  IDLE = 'IDLE',
}

export enum AutoCatStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  IDLE = 'IDLE',
  ERROR = 'ERROR',
}

export enum PlaidUpdateStatus {
  TRANSACTIONS_UPDATE_STATUS_UNKNOWN = 'TRANSACTIONS_UPDATE_STATUS_UNKNOWN',
  NOT_READY = 'NOT_READY',
  INITIAL_UPDATE_COMPLETE = 'INITIAL_UPDATE_COMPLETE',
  HISTORICAL_UPDATE_COMPLETE = 'HISTORICAL_UPDATE_COMPLETE',
}

export type SyncAttentionState = {
  id?: string
  title?: string
  message?: string
  severity?: string
  actionAvailable?: string | null
}

export type SyncStatusAccount = {
  id: number
  name: string | null
  mask: string | null
  transactionCount: number | null
  transactionSyncState: string | null
  updatedAt: string | null
  autoCatStatus: string | null
  autoCatStartedAt: string | null
  autoCatCompletedAt: string | null
  separationCompletedAt: string | null
}

export enum SyncErrorClassification {
  TRANSIENT_RETRYABLE = 'TRANSIENT_RETRYABLE',
  REAUTH_REQUIRED = 'REAUTH_REQUIRED',
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  INSTITUTION_UNSUPPORTED = 'INSTITUTION_UNSUPPORTED',
  INTERNAL_FAILURE = 'INTERNAL_FAILURE',
  USER_SETUP_REQUIRED = 'USER_SETUP_REQUIRED',
  UNKNOWN_PLAID_ERROR = 'UNKNOWN_PLAID_ERROR',
}

export type PlaidItemSyncStatus = {
  plaidItemId: number
  itemId: string
  institutionName: string | null
  needsReconnection: boolean
  transactionSyncStatus: string | null
  transactionSyncTrigger: string | null
  transactionSyncStartedAt: string | null
  transactionSyncCompletedAt: string | null
  transactionSyncErrorAt: string | null
  transactionSyncError: unknown
  syncErrorClassification: SyncErrorClassification | null
  transactionSyncDirty: boolean
  plaidUpdateStatus?: PlaidUpdateStatus | null
  lastTransactionSyncBatchCommittedAt: string | null
  transactionsSyncedCount: number | null
  lastWebhookReceivedAt: string | null
  attentionStates: SyncAttentionState[]
  accounts: SyncStatusAccount[]
}

export type GetAllSyncStatusesResponse = {
  items: PlaidItemSyncStatus[]
  attentionStates: SyncAttentionState[]
}

export const fetchPlaidSyncStatus = (accountCounts = false) =>
  fetchWrapper({
    shouldHandleError: false,
    fetchFunction: () =>
      apiAxios
        .get<GetAllSyncStatusesResponse>('/finances/api/v1/plaid/sync-status', {
          params: accountCounts ? { accountCounts: true } : undefined,
        })
        .then((res) => res.data),
  })

export const fetchPlaidSyncStatusForItem = (plaidItemId: number) => {
  return fetchWrapper({
    shouldHandleError: false,
    fetchFunction: () =>
      apiAxios
        .get<PlaidItemSyncStatus>(
          `/finances/api/v1/plaid/items/${plaidItemId}/sync-status`
        )
        .then((res) => res.data),
  })
}

export const retryPlaidSync = (plaidItemId: number) => {
  return fetchWrapper({
    shouldHandleError: false,
    fetchFunction: () =>
      apiAxios
        .post<PlaidItemSyncStatus>(
          `/finances/api/v1/plaid/items/${plaidItemId}/retry-sync`
        )
        .then((res) => res.data),
  })
}
